import { Link } from 'wouter'
import { useAdminFetch } from '../hooks/useAdminAPI'
import {
  PageHeader, AdminButton, StatusBadge,
  MetricStrip, Metric, Section, DataTable, ViewState,
} from '../components'
import { AdminIcon } from '../components/AdminIcon'
import { deriveAttentionItems, countSourcesByStatus } from '../utils/health'
import { formatDateTime, formatDurationBetween } from '../utils/format'
import styles from './Overview.module.css'

export function Overview() {
  const { data, loading, error, refresh } = useAdminFetch('/admin')

  if (loading && !data) return <ViewState variant="loading" message="Loading dashboard data…" />
  if (error && !data) return <ViewState variant="error" message={error} onRetry={refresh} />
  if (!data) return null

  const sources = data.sourceStats?.sources ?? []
  const runs = data.scrapeRuns ?? []
  const classStats = data.classificationStats
  const embeddings = data.pendingEmbeddings
  const offerHealth = data.offerHealth
  const clusterStats = data.clusterStats

  const sourceCounts = countSourcesByStatus(sources)
  const totalOffers = offerHealth?.total ?? 0
  const latestRun = runs[0]
  const attentionItems = deriveAttentionItems(data)

  // Classification: golden set is the quality gate, agreement rate is diagnostic context
  const latestVersion = classStats?.latestVersion ?? '1'
  const currentRate = classStats?.byVersion?.[latestVersion]?.agreementRate ?? classStats?.agreementRate ?? null
  const goldenSet = classStats?.goldenSet

  const lastRunSubtitle = latestRun
    ? `Last run: ${formatDateTime(latestRun.startedAt)}`
    : undefined

  return (
    <div className={styles.page}>
      <PageHeader
        title="Overview"
        subtitle={lastRunSubtitle}
        actions={
          <AdminButton
            variant="icon-only"
            icon="refresh"
            label="Refresh data"
            onClick={refresh}
            disabled={loading}
          />
        }
      />

      {/* ── Top metrics ───────────────────────────────────────── */}
      <MetricStrip>
        <Metric label="Current offers" value={totalOffers} />
        <Metric
          label="Healthy sources"
          value={`${sourceCounts.healthy}/${sources.length}`}
          variant={sourceCounts.error > 0 ? 'error' : sourceCounts.warning > 0 ? 'warning' : 'success'}
        />
        {goldenSet ? (
          <Metric
            label={`Classifier v${latestVersion}`}
            value={`${goldenSet.passed}/${goldenSet.total}`}
            variant={goldenSet.failed > 0 ? 'error' : 'success'}
          />
        ) : currentRate != null ? (
          <Metric
            label={`Classifier v${latestVersion}`}
            value={`${currentRate}%`}
          />
        ) : null}
        {latestRun && (
          <Metric
            label="Latest run"
            value={latestRun.status}
            variant={latestRun.status === 'failed' ? 'error'
              : latestRun.status === 'partial' ? 'warning'
              : 'success'}
          />
        )}
      </MetricStrip>

      {/* ── Action items ────────────────────────────────────────── */}
      {attentionItems.length > 0 ? (
        <Section title="Your action items">
          <div className={styles.attentionList}>
            {attentionItems.map((item, i) => (
              <Link key={i} href={item.link} className={styles.attentionItem}>
                <span className={`${styles.attentionDot} ${styles[item.severity]}`} />
                <div className={styles.attentionContent}>
                  <span className={styles.attentionTitle}>{item.title}</span>
                  <span className={styles.attentionDetail}>{item.detail}</span>
                </div>
                <span className={styles.attentionAction}>
                  {item.action}
                  <AdminIcon name="chevron-right" size={12} />
                </span>
              </Link>
            ))}
          </div>
        </Section>
      ) : (
        <div className={styles.healthyRow}>
          <AdminIcon name="check" size={16} className={styles.healthyIcon} />
          <span>All systems healthy — nothing needs your attention</span>
        </div>
      )}

      {/* ── Recent runs ───────────────────────────────────────── */}
      {runs.length > 0 && (
        <Section
          title="Recent scrape runs"
          action={<Link href="/runs" className={styles.sectionLink}>View all</Link>}
        >
          <DataTable>
            <thead>
              <tr>
                <th>Status</th>
                <th>Date</th>
                <th>Mode</th>
                <th className={styles.numberCol}>Extracted</th>
                <th className={styles.numberCol}>Accepted</th>
                <th className={styles.numberCol}>Errors</th>
                <th className={styles.numberCol}>Duration</th>
              </tr>
            </thead>
            <tbody>
              {runs.slice(0, 5).map(run => (
                <tr key={run.id} className={run.status === 'failed' ? styles.rowError : run.status === 'partial' ? styles.rowWarning : ''}>
                  <td><StatusBadge status={run.status} /></td>
                  <td className={styles.mono}>{formatDateTime(run.startedAt)}</td>
                  <td className={styles.tertiary}>{run.mode}</td>
                  <td className={styles.number}>{run.extracted}</td>
                  <td className={styles.number}>{run.accepted}</td>
                  <td className={styles.number}>
                    {run.errors > 0
                      ? <span className={styles.errorText}>{run.errors}</span>
                      : '—'
                    }
                  </td>
                  <td className={styles.number}>{formatDurationBetween(run.startedAt, run.completedAt)}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </Section>
      )}

      {/* ── Classification summary ─────────────────────────────── */}
      {classStats && (
        <Section
          title="Classification pipeline"
          action={
            <Link href="/triage" className={styles.sectionLink}>
              View triage
            </Link>
          }
        >
          <MetricStrip>
            {goldenSet && (
              <Metric
                label="Golden set"
                value={`${goldenSet.passed}/${goldenSet.total}`}
                variant={goldenSet.failed > 0 ? 'error' : 'success'}
              />
            )}
            {currentRate != null && (
              <Metric
                label={`v${latestVersion} agreement`}
                value={`${currentRate}%`}
              />
            )}
            <Metric label="Total classified" value={classStats.total ?? 0} />
          </MetricStrip>
          <p className={styles.pipelineHint}>
            Agreement rate compares AI vs keyword fallback — disagreements are normal and expected.
            The golden set is the quality gate.
          </p>
        </Section>
      )}

      {/* ── Embedding pipeline ────────────────────────────────── */}
      {embeddings && (embeddings.pending > 0 || embeddings.failed > 0) && (
        <Section title="Embedding pipeline">
          <MetricStrip>
            <Metric
              label="Pending"
              value={embeddings.pending}
              variant={embeddings.pending > 0 ? 'warning' : 'success'}
            />
            <Metric
              label="Failed"
              value={embeddings.failed}
              variant={embeddings.failed > 0 ? 'error' : 'success'}
            />
          </MetricStrip>
          {embeddings.pending > 0 && (
            <p className={styles.pipelineHint}>
              Pending embeddings process automatically on the next scrape run.
            </p>
          )}
        </Section>
      )}

      {/* ── Offer health ──────────────────────────────────────── */}
      {offerHealth && (offerHealth.insufficientUnitData > 0 || offerHealth.suspiciousDeals > 0) && (
        <Section
          title="Offer health"
          action={<Link href="/offers" className={styles.sectionLink}>View offers</Link>}
        >
          <MetricStrip>
            <Metric label="Total offers" value={offerHealth.total} />
            <Metric
              label="Missing unit data"
              value={offerHealth.insufficientUnitData}
              variant={offerHealth.insufficientUnitData > 0 ? 'warning' : 'success'}
            />
            <Metric
              label="Suspicious deals"
              value={offerHealth.suspiciousDeals}
              variant={offerHealth.suspiciousDeals > 0 ? 'warning' : 'success'}
            />
          </MetricStrip>
        </Section>
      )}

      {/* ── Cluster inventory ─────────────────────────────────── */}
      {clusterStats && (
        <Section
          title="Clusters"
          action={<Link href="/clusters" className={styles.sectionLink}>View all</Link>}
        >
          <MetricStrip>
            <Metric label="Clusters" value={clusterStats.totalClusters} />
            <Metric label="Products" value={clusterStats.totalProducts} />
            <Metric label="Aliases" value={clusterStats.totalAliases} />
          </MetricStrip>
          {clusterStats.byCategory && (
            <div className={styles.breakdownGroup}>
              {Object.entries(clusterStats.byCategory).map(([cat, count]) => (
                <div key={cat} className={styles.breakdownRow}>
                  <span className={styles.breakdownLabel}>
                    <span className={`${styles.categoryDot} ${styles[`cat_${cat}`]}`} />
                    {cat}
                  </span>
                  <span className={styles.breakdownValue}>{count}</span>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}
    </div>
  )
}
